import { createElement, elementHasContent } from "../utils/element-handlers.utils";

type NobuVideoUserCallbackFunction = (eventType: NobuVideoEventTypes) => void;
type NobuVideoEventTypes = 'play' | 'pause' | 'stop' | 'mute' | 'unmute' | 'ended' | 'loop';

class NobuVideo {
    private initialized: boolean = false;
    private muted: boolean = true;
    private onUpdateUserCallbackList: NobuVideoUserCallbackFunction[] = [];
    private preventNativePlayCallback: boolean = false;
    private preventNativePauseCallback: boolean = false;
    private videoElement: HTMLVideoElement | null = null;
    private playPauseButton: HTMLElement | null = null;
    private soundOnOffButton: HTMLElement | null = null;

    constructor(
        videoElement?: HTMLVideoElement,
        playPauseButton?: HTMLElement | null,
        soundOnOffButton?: HTMLElement | null
    ) {
        const self = this;
        self.videoElement = videoElement || null;

        if (self.videoElement) {
            if (playPauseButton) {
                self.initPlayPauseButton(playPauseButton);
            }
    
            if (soundOnOffButton) {
                self.initMuteButton(soundOnOffButton);
            }
    
            self.videoElement.addEventListener('play', function () {
                if (self.preventNativePlayCallback) {
                    self.preventNativePlayCallback = false;
                }
                else {
                    self.privateUpdateCallback('play');
                }
            });
    
            self.videoElement.addEventListener('pause', function () {
                if (self.preventNativePauseCallback) {
                    self.preventNativePauseCallback = false;
                }
                else {
                    self.privateUpdateCallback('pause');
                }
            });

            self.videoElement.addEventListener('ended', () => {
                self.privateUpdateCallback('ended');
            })
    
            self.muted = self.videoElement.muted;
            self.initialized = true;
        }
    }

    private initPlayPauseButton(playPauseButtonElement: HTMLElement) {
        const self = this;
        self.playPauseButton = playPauseButtonElement;

        var playPauseButtonHasOwnContent = elementHasContent(self.playPauseButton);

        if (!playPauseButtonHasOwnContent) {
            var playPauseButtonInnerElement = createElement('span', 'n-video-playpause-inner', [
                createElement('span', 'n-video-playpause-pause', [
                    createElement('img', {alt: 'Pause video', src: './themes/nobu/images/icon-pause.svg'}),
                    createElement('span', null, 'Pause video')
                ]),
                createElement('span', 'n-video-playpause-play', [
                    createElement('img', {alt: 'Play video', src: './themes/nobu/images/icon-play.svg'}),
                    createElement('span', null, 'Play video')
                ])
            ]);

            self.playPauseButton.appendChild(playPauseButtonInnerElement);
        }

        self.playPauseButton.setAttribute('data-video-play', String(self.isPlaying()));
        self.playPauseButton.addEventListener('click', function (event) {
            event.preventDefault();

            if (self.isPlaying()) {
                self.pause();
            }
            else {
                self.play();
            }
        });
    }

    private initMuteButton(soundOnOffButtonElement: HTMLElement) {
        const self = this;

        self.soundOnOffButton = soundOnOffButtonElement;
        const soundOnOffButtonHasOwnContent = elementHasContent(self.soundOnOffButton);

        if (!soundOnOffButtonHasOwnContent) {
            var muteButtonInnerElement = createElement('span', 'n-video-mute-inner', [
                createElement('span', 'n-video-mute-muted', [
                    createElement('img', {alt: 'Muted video', src: './themes/nobu/images/icon-muted.svg'})
                ]),
                createElement('span', 'n-video-mute-unmuted', [
                    createElement('img', {alt: 'Unmuted video', src: './themes/nobu/images/icon-unmuted.svg'})
                ])
            ]);

            self.soundOnOffButton.appendChild(muteButtonInnerElement);
        }

        self.soundOnOffButton.setAttribute('data-video-muted', String(self.muted));
        self.soundOnOffButton.addEventListener('click', function (event) {
            event.preventDefault();

            if (self.muted) {
                self.unmute();
            }
            else {
                self.mute();
            }
        });
    }

    private privateUpdateCallback(eventType: NobuVideoEventTypes) {
        if (this.playPauseButton) {
            this.playPauseButton.setAttribute('data-video-play', String(this.isPlaying()));
        }

        if (this.soundOnOffButton) {
            this.soundOnOffButton.setAttribute('data-video-muted', String(this.muted));
        }

        // if (this.onChangePlayUserCallback) {
        //     this.onChangePlay(this.onChangePlayUserCallback);
        // }

        if (this.onUpdateUserCallbackList.length > 0) {
            this.onUpdateUserCallbackList.forEach(function (onUpdateUserCallbackFn) {
                onUpdateUserCallbackFn(eventType);
            });
        }
    }

    public play(successCallback?: NobuVideoUserCallbackFunction) {
        if (!this.initialized) return;

        if (this.videoElement && !this.isPlaying()) {
            this.preventNativePlayCallback = true;

            if (this.videoElement.paused) {
                this.videoElement.play();
            }

            if (successCallback) successCallback('play');

            this.privateUpdateCallback('play');
        }
    }

    public pause(successCallback?: NobuVideoUserCallbackFunction) {
        if (!this.initialized) return;
        
        if (this.videoElement && this.isPlaying()) {
            this.preventNativePauseCallback = true;

            if (!this.videoElement.paused) {
                this.videoElement.pause();
            }

            if (successCallback) successCallback('pause');

            this.privateUpdateCallback('pause');
        }
    }

    public stop(successCallback?: NobuVideoUserCallbackFunction) {
        if (!this.initialized) return;
        
        if (this.videoElement && this.isPlaying()) {
            this.preventNativePauseCallback = true;

            if (!this.videoElement.paused) {
                this.videoElement.pause();
            }

            this.videoElement.currentTime = 0;

            if (successCallback) successCallback('stop');

            this.privateUpdateCallback('stop');
        }
    }

    public mute(successCallback?: NobuVideoUserCallbackFunction) {
        if (!this.initialized) return;

        if (this.videoElement && !this.muted) {
            this.muted = true;

            if (!this.videoElement.muted) {
                this.videoElement.muted = this.muted;
            }

            if (successCallback) successCallback('mute');

            this.privateUpdateCallback('mute');
        }
    }

    public unmute(successCallback?: NobuVideoUserCallbackFunction) {
        if (!this.initialized) return;

        if (this.videoElement && this.muted) {
            this.muted = false;

            if (this.videoElement.muted) {
                this.videoElement.muted = this.muted;
            }

            if (successCallback) successCallback('unmute');

            this.privateUpdateCallback('unmute');
        }
    }

    public hasAudio() {
        const videoElementAny: any = this.videoElement;

        if (videoElementAny) {
            if (typeof videoElementAny['webkitAudioDecodedByteCount'] !== 'undefined') {
                if (videoElementAny['webkitAudioDecodedByteCount'] === 0) {
                    return false;
                }
            }
            else if (typeof videoElementAny['mozHasAudio'] !== 'undefined') {
                if (!videoElementAny['mozHasAudio']) {
                    return false;
                }
            }

            return true;
        }

        return false;
    }

    public element() {
        return this.videoElement;
    };

    public isPlaying() {
        return this.videoElement ? !this.videoElement.paused : false;
    }

    public isMuted() {
        return this.muted;
    };

    public onUpdate(updateUserCallback: NobuVideoUserCallbackFunction) {
        const self = this;
        self.onUpdateUserCallbackList.push(updateUserCallback);

        return function deleteEventCallback() {
            self.onUpdateUserCallbackList.splice(
                self.onUpdateUserCallbackList.indexOf(updateUserCallback), 1
            );
        }
    };
}

export default NobuVideo;
